// Pollution just for ICE (Internal Combustion Engine) with Particulate Matter
// Lifted almost identically from CO2 Pollution file
// Sheet!PM Pollution
import { zipWith } from 'lodash';
import { createSelector } from 'reselect';

import {
  // ICE Items
  getCNGPMEmissionsPerKm,
  getDieselPMEmissionsPerKm,
  getGasolinePMEmissionsPerKm,
} from './pmEmissionRate';

// Gasoline travel lives not in vehicleDistanceTraveled because of a circular dependency.
import {
  getUtilizationCNG,
  getUtilizationDiesel,
  getUtilizationGasoline,
} from '../vehicleStock';

// Magic constant 10 ^ 9 / 1000 / 1000 / 10 ^ 6 to go from g/km to MTons
// Note we are multiplying the subcomponents first rather than in batch at the end to match
// the spreadsheet, whereas the other spreadsheet applies the scaleFactor last.
const SCALE_FACTOR = .001;

export const getGasolineEmissionsTotal = createSelector([
  getUtilizationGasoline,
  getGasolinePMEmissionsPerKm,
],
  (distanceSeries, emissionsSeries, ) => {
    return zipWith(
      distanceSeries,
      emissionsSeries,
      (distance, emissions) => {
        return {
          autoRickshaw: {
            commercial: emissions.autoRickshaw.commercial * distance.autoRickshaw.commercial * SCALE_FACTOR,
          },
          car: {
            commercial: emissions.car.commercial * distance.car.commercial * SCALE_FACTOR,
            private: emissions.car.private * distance.car.private * SCALE_FACTOR,

          },
          publicTransit: {
            bus: emissions.publicTransit.bus * distance.publicTransit.bus * SCALE_FACTOR,
          },
          twoWheel: {
            commercial: emissions.twoWheel.commercial * distance.twoWheel.commercial * SCALE_FACTOR,
            private: emissions.twoWheel.private * distance.twoWheel.private * SCALE_FACTOR,
          }
        }
      }
    )
  }
)


export const getCNGEmissionsTotal = createSelector([
  getUtilizationCNG,
  getCNGPMEmissionsPerKm,
],
  (distanceSeries, emissionsSeries, ) => {
    return zipWith(
      distanceSeries,
      emissionsSeries,
      (distance, emissions) => {
        return {
          autoRickshaw: {
            commercial: emissions.autoRickshaw.commercial * distance.autoRickshaw.commercial * SCALE_FACTOR,
          },
          car: {
            commercial: emissions.car.commercial * distance.car.commercial * SCALE_FACTOR,
            private: emissions.car.private * distance.car.private * SCALE_FACTOR,

          },
          publicTransit: {
            bus: emissions.publicTransit.bus * distance.publicTransit.bus * SCALE_FACTOR,
          },
        }
      }
    )
  }
)

export const getDieselEmissionsTotal = createSelector([
  getUtilizationDiesel,
  getDieselPMEmissionsPerKm,
],
  (distanceSeries, emissionsSeries) => {
    return zipWith(
      distanceSeries,
      emissionsSeries,
      (distance, emissions) => {
        return {
          autoRickshaw: {
            commercial: emissions.autoRickshaw.commercial * distance.autoRickshaw.commercial * SCALE_FACTOR,
          },
          car: {
            commercial: emissions.car.commercial * distance.car.commercial * SCALE_FACTOR,
            private: emissions.car.private * distance.car.private * SCALE_FACTOR,

          },
          publicTransit: {
            bus: emissions.publicTransit.bus * distance.publicTransit.bus * SCALE_FACTOR,
          },
        }
      }
    )
  }
)

// Combine all the ICE Sources
export const getPMEmissionsSeries = createSelector([
  getGasolineEmissionsTotal,
  getDieselEmissionsTotal,
  getCNGEmissionsTotal
],
  (gasolineSeries, dieselSeries, CNGSeries) => {
    return zipWith(
      gasolineSeries,
      dieselSeries,
      CNGSeries,
      (gasoline, diesel, CNG) => {
        return {
          autoRickshaw: {
            commercial: (gasoline.autoRickshaw.commercial + diesel.autoRickshaw.commercial + CNG.autoRickshaw.commercial),
          },
          car: {
            commercial: (gasoline.car.commercial + diesel.car.commercial + CNG.car.commercial),
            private: (gasoline.car.private + diesel.car.private + CNG.car.private),

          },
          publicTransit: {
            bus: (gasoline.publicTransit.bus + diesel.publicTransit.bus + CNG.publicTransit.bus),
            // metro is electric only
          },
          twoWheel: { // only one fuel type
            commercial: gasoline.twoWheel.commercial,
            private: gasoline.twoWheel.private,
          }
        }
      }
    )
  }
)

// Make a sum!
const getTotalFromPMMapping = (entry: any) => {
  return (
    entry.autoRickshaw.commercial +
    entry.car.commercial +
    entry.car.private +
    entry.twoWheel.commercial +
    entry.twoWheel.private +
    entry.publicTransit.bus
  )
}

// Export for graph
export const getPMEmissionsTotalSeries = createSelector([
  getPMEmissionsSeries
],
  (pmEmissionsSeries) => {
    return pmEmissionsSeries.map(getTotalFromPMMapping);
  }
)

export const getPMEmissionsTotal = createSelector([
  getPMEmissionsTotalSeries
],
  (pmEmissionsSeries) => {
    return pmEmissionsSeries.slice(-1)[0];
  }
)



// WEBPACK FOOTER //
// ./src/store/selectors/pmPollution/index.ts